import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import { Palmtree, ClipboardList, Users, Sun, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Muestra los empleados ausentes hoy (vacaciones en rango + permisos del día).
 * Props:
 *   supervisorUserId — si se pasa, filtra solo los empleados de ese supervisor
 */
export default function TodayAbsences({ supervisorUserId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = supervisorUserId ? { supervisor_id: supervisorUserId } : {};
    adminAPI.getTodayAbsences(params)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [supervisorUserId]);

  const todayLabel = format(new Date(), "EEEE d 'de' MMMM", { locale: es });
  // Capitalizar primera letra
  const todayStr = todayLabel.charAt(0).toUpperCase() + todayLabel.slice(1);

  return (
    <div className="card" style={{ marginTop: 20 }}>
      {/* Header */}
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sun size={17} color="var(--warning)" strokeWidth={1.8} />
          <div>
            <div className="card-title">Ausencias de hoy</div>
            <div className="card-subtitle">{todayStr}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {data && (
            <span style={{
              fontSize: 12, fontWeight: 700,
              padding: '2px 10px', borderRadius: 12,
              background: data.total > 0 ? '#fef3c7' : 'var(--gray-100)',
              color: data.total > 0 ? '#92400e' : 'var(--gray-500)',
            }}>
              {data.total} ausente{data.total !== 1 ? 's' : ''}
            </span>
          )}
          <button onClick={load}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', display: 'flex', padding: 4, borderRadius: 6 }}
            title="Actualizar">
            <RefreshCw size={14} strokeWidth={2} />
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: 'var(--gray-400)' }}>
          Cargando…
        </div>
      ) : !data || data.total === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Users size={32} color="var(--gray-300)" style={{ margin: '0 auto 8px', display: 'block' }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--gray-500)' }}>
            Sin ausencias hoy
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
            Todos los empleados están presentes
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

          {/* Vacaciones */}
          {data.vacations.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                De vacaciones ({data.vacations.length})
              </div>
              {data.vacations.map((emp, i) => (
                <AbsenceRow
                  key={`vac-${i}`}
                  emp={emp}
                  type="vacation"
                  detail={`${fmtDate(emp.start_date)} → ${fmtDate(emp.end_date)}  ·  ${emp.total_days}d`}
                />
              ))}
            </>
          )}

          {/* Permisos */}
          {data.leaves.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: data.vacations.length > 0 ? 10 : 0, marginBottom: 4 }}>
                Con permiso ({data.leaves.length})
              </div>
              {data.leaves.map((emp, i) => (
                <AbsenceRow
                  key={`leave-${i}`}
                  emp={emp}
                  type="leave"
                  detail={emp.duration}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const fmtDate = (d) => {
  try { return format(new Date(d + 'T00:00:00'), 'dd/MM'); } catch { return d; }
};

function AbsenceRow({ emp, type, detail }) {
  const isVac  = type === 'vacation';
  const Icon   = isVac ? Palmtree : ClipboardList;
  const color  = isVac ? '#2563eb' : '#7c3aed';
  const bg     = isVac ? '#eff6ff' : '#f5f3ff';

  // Iniciales
  const initials = `${(emp.first_name?.[0] || '').toUpperCase()}${(emp.last_name?.[0] || '').toUpperCase()}`;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px', borderRadius: 8,
      background: bg,
    }}>
      {/* Avatar iniciales */}
      <div style={{
        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
        background: color, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700,
      }}>
        {initials}
      </div>

      {/* Nombre y depto */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-900)' }}>
          {emp.first_name} {emp.last_name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          {emp.department || '—'}
        </div>
      </div>

      {/* Detalle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        <Icon size={13} color={color} strokeWidth={2} />
        <span style={{ fontSize: 12, fontWeight: 500, color }}>
          {detail}
        </span>
      </div>
    </div>
  );
}
